/* Auth store + actions. Wired to real /api/v1 endpoints. */

import { writable, type Writable } from "svelte/store";
import { api, ApiError } from "./api";
import { disconnectRealtime } from "./realtime";

export type TenantInfo = {
  id: number;
  slug: string;
  display_name: string;
  /** Primary position in the supply chain — the head of `company_types`. */
  company_type: string;
  /** Every position the company occupies. Read this wherever "can we do X?"
   *  is the question; `company_type` only answers "what are we mainly?". */
  company_types?: string[];
  /** "orders" / "explore": the short workspace an invited client chose at
   *  signup; "" = the full one. Read through `$lib/surfaces.workspaceOf`. */
  workspace?: string;
  status: string;
  country: string;
  default_currency: string;
  default_vat_rate_pct: number;
  /** Decimals a catalogue price is written with — 2 (12.00) or 3 (12.000). */
  price_decimals?: number;
  /** Prices → Settings opt-in: show the cost + coefficient columns in the
   *  price matrix (per-client profit view). Off = base sale price only. */
  show_price_formation?: boolean;
  /** Positions suppliers open are added to our Products automatically. */
  auto_import_supplier_products?: boolean;
  /** How prices are written on each surface that shows one — Settings →
   *  «Отображение цен». Read through `$lib/priceDisplay`, never field by
   *  field: the currency only applies when its own tick is on. */
  public_prices_include_vat?: boolean;
  public_prices_local_currency?: boolean;
  public_price_currency?: string;
  client_prices_include_vat?: boolean;
  client_prices_local_currency?: boolean;
  client_price_currency?: string;
  export_prices_include_vat?: boolean;
  export_prices_local_currency?: boolean;
  export_price_currency?: string;
  stock_prices_include_vat?: boolean;
  stock_prices_local_currency?: boolean;
  stock_price_currency?: string;
  /** The rate those conversions run at, when the company pinned one. */
  fx_rate_fixed?: boolean;
  fx_rate_fixed_value?: number | null;
  fx_rate_fixed_at?: string | null;
  timezone: string;
  banks_used: string;
  lat: number | null;
  lng: number | null;
};

/** Plan ceiling for a tenant, embedded in /me alongside permissions.
 * Gate UI on `is_pro` / `features.*.allowed` — not `plan` (an expired trial
 * still reports plan="pro" but is_pro=false). Mirrors
 * apps.common.entitlements.entitlements_for on the backend. */
export type Entitlements = {
  plan: string;
  status: string;
  trial_ends_at: string | null;
  /** Paid-through date for the current subscription period ("paid up to"). */
  current_period_end: string | null;
  is_pro: boolean;
  /** Set while the subscription is paused for a season. */
  paused_until?: string | null;
  features: {
    purchase_orders?: { allowed: boolean };
    publish_catalog?: { allowed: boolean };
    sales_pricing?: { allowed: boolean };
    accept_orders?: { allowed: boolean };
    /** The six constructor modules (apps/billing/catalog.MODULE_FEATURES). */
    public_page?: { allowed: boolean };
    warehouse?: { allowed: boolean };
    accounting?: { allowed: boolean };
    reports?: { allowed: boolean };
    dashboard_mgmt?: { allowed: boolean };
    partner_search?: { allowed: boolean };
    pricing: { allowed: boolean };
    orders: {
      allowed: boolean;
      limit: number | null;
      used: number | null;
      remaining: number | null;
    };
  };
};

export type TenantCapabilities = {
  version: number;
  /** buyer | seller | catalog_owner | public_profile — derived from active
   *  SupplierLink directions, never from company type alone. */
  capabilities: string[];
  surface_version?: number;
  /** Role adaptation: which sections of the app this company gets at all
   *  ("nav.orders", "partners.suppliers", …). See $lib/surfaces and
   *  apps.tenants.surfaces. Absent on sessions cached before it shipped. */
  surfaces?: string[];
  defaults: {
    orders_direction: "incoming" | "outgoing";
    partners_tab: "clients" | "suppliers";
  };
};

export type Membership = {
  tenant: TenantInfo;
  role: "owner" | "manager" | "procurement" | "sales" | "warehouse" | "accounting" | "viewer";
  permission_version?: number;
  permission_revision?: number;
  actions?: Record<string, boolean>;
  /** Which customers a sales employee sees: everyone, domestic, export, or
   *  only the ones they are attached to. Attached customers show in every mode. */
  sales_scope?: { mode: "all" | "domestic" | "export" | "assigned" };
  is_active: boolean;
  permissions: Record<string, { view: boolean; edit: boolean; delete: boolean }>;
  entitlements?: Entitlements;
  capabilities?: TenantCapabilities;
  link_tou_accepted_at: string | null;
};

/** The notification e-mails a person can switch on in «Мой профиль», in the
 *  order they are listed (backend: `apps.users.notifications.CATEGORIES`). */
export const EMAIL_NOTIFICATIONS = ["orders", "partners", "supplier_updates", "subscription"] as const;

export type AuthUser = {
  id: number;
  email: string;
  full_name: string;
  phone: string;
  share_phone_in_partner_invites: boolean;
  language: string;
  /** Notification e-mails this person asked for — see EMAIL_NOTIFICATIONS.
   *  All off by default: only account mail (password, e-mail change) is sent. */
  email_notifications?: Record<string, boolean>;
  is_super_admin: boolean;
  /** May open the Backoffice. True for super-admins too (User.is_platform_staff). */
  is_staff: boolean;
  onboarding_completed_at: string | null;
  /** True when the wizard was abandoned rather than finished — the only case
   *  where the top-bar menu keeps its "Get started" shortcut. */
  onboarding_skipped: boolean;
  email_verified: boolean;
  /** Null until the person accepted the Terms of Use and Privacy Policy;
   *  App.svelte shows the blocking TermsGate while it is null. */
  terms_accepted_at: string | null;
};

export type AuthState = {
  authenticated: boolean;
  loading: boolean;
  user?: AuthUser;
  memberships: Membership[];
  activeTenantSlug?: string;
};

export const auth: Writable<AuthState> = writable({
  authenticated: false,
  loading: true,
  memberships: [],
});

type MeResponse = { user: AuthUser; memberships: Membership[] };

export function clearSession(): void {
  disconnectRealtime();
  auth.set({ authenticated: false, loading: false, memberships: [] });
}

export async function loadSession(): Promise<void> {
  try {
    const data = await api.get<MeResponse>("/me");
    auth.set({
      authenticated: true,
      loading: false,
      user: data.user,
      memberships: data.memberships,
      activeTenantSlug: data.memberships[0]?.tenant.slug,
    });
  } catch {
    clearSession();
  }
}

export const refreshMe = loadSession;

/** Refresh plan changes without logging out on a transient network failure. */
export async function refreshAccess(): Promise<void> {
  try {
    const data = await api.get<MeResponse>("/me");
    auth.update((current) => current.authenticated
      ? { ...current, user: data.user, memberships: data.memberships }
      : current);
  } catch { /* keep the current session; the server still enforces access */ }
}

export async function login(
  email: string,
  password: string,
  remember: boolean,
): Promise<MeResponse> {
  disconnectRealtime();
  const data = await api.post<MeResponse>("/auth/login", { email, password, remember });
  auth.set({
    authenticated: true,
    loading: false,
    user: data.user,
    memberships: data.memberships,
    activeTenantSlug: data.memberships[0]?.tenant.slug,
  });
  return data;
}

export async function logout(): Promise<void> {
  try {
    await api.post("/auth/logout");
  } catch (err) {
    if (!(err instanceof ApiError && err.status === 401)) {
      throw err;
    }
  } finally {
    clearSession();
  }
}

/** Stamp onboarding as done. Keeps the store in sync so Bootstrap/Login stop
 * routing into the wizard. `skipped` marks it abandoned rather than finished,
 * which is what keeps the shortcut in the top-bar menu. */
/** The blocking dialog's «I agree»: stamps terms_accepted_at and updates the
 *  store in place so the gate drops without a full /me round-trip. */
export async function acceptTerms(): Promise<void> {
  await api.post("/me/accept-terms", {});
  auth.update((s) =>
    s.user ? { ...s, user: { ...s.user, terms_accepted_at: new Date().toISOString() } } : s,
  );
}

export async function completeOnboarding(skipped = false): Promise<void> {
  await api.post(`/me/complete-onboarding?skipped=${skipped}`);
  auth.update((s) =>
    s.user
      ? {
          ...s,
          user: {
            ...s.user,
            onboarding_completed_at: new Date().toISOString(),
            onboarding_skipped: skipped,
          },
        }
      : s,
  );
}

export async function acceptInvitation(data: {
  token: string;
  password: string;
  full_name: string;
  accept_terms: boolean;
}): Promise<any> {
  return api.post("/invitations/accept", data);
}
